import React, { useState } from 'react'
import { IoFlaskOutline, IoChevronForwardOutline, IoPlayOutline } from 'react-icons/io5'
import { Widget } from '../Widget'
import { useStrategies, runStrategy } from '../../../swr-config'
import type { PageId } from '../../BottomNav'
import type { BetStrategy } from '../../../types'

interface StrategyAlertsWidgetProps {
  isEditing?: boolean
  onRemove?: () => void
  onNavigate?: (page: PageId) => void
}

export function StrategyAlertsWidget({
  isEditing,
  onRemove,
  onNavigate,
}: StrategyAlertsWidgetProps) {
  const { data, error, isLoading } = useStrategies(true)
  const [runningId, setRunningId] = useState<number | null>(null)
  const [runResults, setRunResults] = useState<Record<number, number>>({})

  const strategies = data?.strategies || []

  const handleStrategyClick = (strategy: BetStrategy) => {
    // Navigate to strategies page
    onNavigate?.('strategies')
  }

  const handleRunStrategy = async (e: React.MouseEvent, strategy: BetStrategy) => {
    e.stopPropagation()
    if (runningId) return

    setRunningId(strategy.id)
    try {
      const result = await runStrategy(strategy.id)
      setRunResults((prev) => ({ ...prev, [strategy.id]: result.total_matches }))
    } catch (err) {
      console.error('Failed to run strategy:', err)
    } finally {
      setRunningId(null)
    }
  }

  return (
    <Widget
      id="alerts"
      title="My Strategies"
      icon={<IoFlaskOutline size={18} />}
      linkTo="strategies"
      isEditing={isEditing}
      onRemove={onRemove}
      onNavigate={onNavigate}
      isLoading={isLoading}
      error={error ? 'Failed to load strategies' : null}
      skeletonVariant="alerts"
    >
      <div className="widget-alerts">
        {strategies.length === 0 ? (
          <div className="widget-empty">
            <p>No strategies yet</p>
            <button
              className="widget-empty-action"
              onClick={() => onNavigate?.('strategies')}
            >
              Create a strategy
            </button>
          </div>
        ) : (
          <ul className="widget-alerts-list">
            {strategies.slice(0, 5).map((strategy) => (
              <li
                key={strategy.id}
                className="widget-alert-item"
                onClick={() => handleStrategyClick(strategy)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleStrategyClick(strategy)
                  }
                }}
              >
                <div className="widget-alert-info">
                  <span className="widget-alert-name">{strategy.name}</span>
                  {runResults[strategy.id] !== undefined && (
                    <span className="widget-alert-badge">
                      {runResults[strategy.id]} matches
                    </span>
                  )}
                </div>
                <div className="widget-alert-meta">
                  <button
                    className="widget-strategy-run-btn"
                    onClick={(e) => handleRunStrategy(e, strategy)}
                    disabled={runningId === strategy.id}
                    title="Run strategy"
                  >
                    {runningId === strategy.id ? (
                      <span className="widget-strategy-spinner" />
                    ) : (
                      <IoPlayOutline size={16} />
                    )}
                  </button>
                  <IoChevronForwardOutline size={16} className="widget-alert-arrow" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Widget>
  )
}
